import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, FileText, Info, Loader2, Paperclip, TriangleAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Combobox } from '../../../components/ui/combobox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import AccessRestricted from '../../../components/AccessRestricted';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { Branch, Project, Vendor } from '../../../types/lists.types';
import { ProcurementApprovalRule } from '../../../types/procurement.types';
import { Product } from '../../../types/stock.types';
import { ApiError } from '../../../utils/api';
import { branchesApi, projectsApi, vendorsApi } from '../../../utils/lists-api';
import { procurementSettingsApi, purchaseOrdersApi } from '../../../utils/procurement-api';
import { productsApi } from '../../../utils/stock-api';
import { usePageTitle } from '../../../utils/usePageTitle';
import { money, orderReference } from './procurement-ui';
import PurchaseOrderLineEditor, {
    emptyLineDraft,
    lineDraftPayloads,
    orderDraftIssue,
    orderDraftTotal,
    PurchaseOrderLineDraft,
} from './components/PurchaseOrderLineEditor';

/**
 * New Purchase Order (a request — it saves as a draft).
 *
 * Branch first, then the project (filtered to that branch, because a project belongs to a
 * branch), then the vendor. The lines are picked from the Product Library or typed as
 * custom lines; either way the order carries a price, because an unpriced order has nothing
 * for the approval rule to measure.
 *
 * The approval threshold is only visible to whoever can read procurement settings, so most
 * requesters never see it: this page compares against it when it happens to be known and
 * otherwise explains that the rule is applied — and the amount snapshotted — at submit.
 * The server is the authority either way.
 */

/** A quotation is usually one PDF; ten is a generous ceiling, not a target. */
const MAX_ATTACHMENTS = 10;

const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function PurchaseOrderFormPage() {
    usePageTitle();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.PURCHASE_ORDER);

    const [branchId, setBranchId] = useState('');
    const [projectId, setProjectId] = useState('');
    const [vendorId, setVendorId] = useState('');
    const [requiredByDate, setRequiredByDate] = useState('');
    const [notes, setNotes] = useState('');
    const [lines, setLines] = useState<PurchaseOrderLineDraft[]>([emptyLineDraft(false)]);
    /** Quotation / order paperwork held locally until the draft exists to attach it to. */
    const [attachments, setAttachments] = useState<File[]>([]);
    const attachmentInput = useRef<HTMLInputElement | null>(null);

    const [branches, setBranches] = useState<Branch[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [rules, setRules] = useState<ProcurementApprovalRule[]>([]);
    const [saving, setSaving] = useState<'draft' | 'submit' | null>(null);

    useEffect(() => {
        if (!canCreate) return;
        branchesApi.list({ status: 'active' }).then(setBranches).catch(() => undefined);
        vendorsApi.list({ status: 'active' }).then(setVendors).catch(() => undefined);
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        // A requester usually cannot read procurement settings and the API client keeps 403s
        // quiet, so a refusal here is the normal case: the threshold notice falls back to
        // explaining the submit-time rule.
        procurementSettingsApi.get()
            .then((settings) => setRules(settings.approvalRules))
            .catch(() => undefined);
    }, [canCreate]);

    // Projects belong to a branch — a branch change invalidates the pick.
    useEffect(() => {
        if (!canCreate || !branchId) {
            setProjects([]);
            return;
        }
        projectsApi.list({ status: 'active', branchId }).then(setProjects).catch(() => undefined);
    }, [canCreate, branchId]);

    if (!canCreate) return <AccessRestricted />;

    const pricedLines = lineDraftPayloads(lines);
    const total = orderDraftTotal(lines);
    const linesIssue = orderDraftIssue(lines);
    const branch = branches.find((b) => b.id === Number(branchId)) ?? null;

    const activeRules = rules.filter((rule) => rule.isActive);
    const threshold = branchId
        ? activeRules.find((rule) => rule.branchId === Number(branchId))?.thresholdAmount
            ?? activeRules.find((rule) => rule.branchId === null)?.thresholdAmount
            ?? null
        : null;

    const draftReady = Boolean(branchId) && attachments.length > 0
        && pricedLines.length > 0 && !linesIssue && saving === null;
    const submitReady = draftReady && Boolean(vendorId);

    const blockReason = !branchId
        ? 'Pick a branch.'
        : pricedLines.length === 0
            ? 'Add at least one line with a description and a quantity.'
            : linesIssue
                ?? (attachments.length === 0
                    ? 'Attach at least one quotation or supporting document.'
                    : vendorId ? null : 'Add a vendor to submit — a draft can be saved without one.');

    const notice = threshold !== null
        ? (total >= threshold
            ? {
                tone: 'amber' as const,
                title: 'This will need approval before AP sees it',
                body: `${money(total)} is at or above the ${branch?.name ?? 'branch'} threshold of ` +
                    `${money(threshold)}. The rule is snapshotted at submit, so a later threshold ` +
                    `change will not rewrite this request.`,
            }
            : {
                tone: 'blue' as const,
                title: 'No approval needed at this value',
                body: `${money(total)} is under the ${branch?.name ?? 'branch'} threshold of ` +
                    `${money(threshold)}. On submit it goes straight to AP to be issued.`,
            })
        : {
            tone: 'blue' as const,
            title: `${money(total)} so far`,
            body: 'The approval rule is applied at submit and the amount is snapshotted then, so ' +
                'this request is judged on the value it has when you submit it. The server is the ' +
                'authority — this is only what is known here.',
        };

    const serverMessage = (err: unknown, fallback: string) => {
        if (err instanceof ApiError) {
            // Error bodies are `{ message }` — see the backend route conventions.
            const body = err.data as { message?: string } | null;
            return body?.message || err.message || fallback;
        }
        return err instanceof Error ? err.message || fallback : fallback;
    };

    /** Queue paperwork on the form; the upload happens once the draft has an id. */
    const addAttachmentFiles = (picked: FileList | null) => {
        if (!picked?.length) return;
        const incoming = Array.from(picked);
        if (attachments.length + incoming.length > MAX_ATTACHMENTS) {
            toast.error(`Up to ${MAX_ATTACHMENTS} files — the extra one(s) were ignored.`);
        }
        setAttachments((prev) => [...prev, ...incoming].slice(0, MAX_ATTACHMENTS));
    };

    /** Create the draft, then submit it when asked. A failed submit leaves the draft behind. */
    const save = async (submit: boolean) => {
        if (!draftReady || (submit && !vendorId)) return;
        try {
            setSaving(submit ? 'submit' : 'draft');
            const order = await purchaseOrdersApi.create({
                branchId: Number(branchId),
                projectId: projectId ? Number(projectId) : null,
                vendorId: vendorId ? Number(vendorId) : null,
                requiredByDate: requiredByDate || null,
                notes: notes.trim() || null,
                lines: pricedLines,
            });

            // The draft has to exist before paperwork can hang off it, so the uploads run
            // after the create. A failure must not lose the order — warn and carry on to the
            // record page, but do not submit an order that is missing its required quotation.
            let attachmentsComplete = true;
            if (attachments.length > 0) {
                try {
                    for (const file of attachments) {
                        await purchaseOrdersApi.uploadDocument(order.id, file);
                    }
                } catch {
                    attachmentsComplete = false;
                    toast.error(
                        `${orderReference(order)} was created, but the quotation failed to ` +
                        `upload — attach it again from the order page.`);
                }
            }

            if (submit && attachmentsComplete) {
                try {
                    const submitted = await purchaseOrdersApi.submit(order.id);
                    toast.success(orderReference(submitted) +
                        (submitted.status === 'pending_approval'
                            ? ' submitted — routed for approval'
                            : ' submitted to AP'));
                } catch (err) {
                    toast.error(`Draft saved, but the submit failed: ${serverMessage(err, 'unknown error')}`);
                }
            } else if (!submit && attachmentsComplete) {
                toast.success(`${orderReference(order)} saved as a draft`);
            }
            setSaving(null);
            navigate(`/stock/procurement/purchase-orders/${order.id}`);
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to create the purchase order'));
            setSaving(null);
        }
    };

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
            <div>
                <Button variant="ghost" size="sm" className="mb-2 -ml-2"
                        onClick={() => navigate('/stock/procurement/purchase-orders')}>
                    <ArrowLeft size={16} className="mr-1.5" />Purchase Orders
                </Button>
                <h1 className="text-2xl font-bold">New Purchase Order</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Raise a request against a branch. It saves as a draft until you submit it —
                    then the approval rule decides whether it goes to an approver or straight to AP.
                </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); save(true); }} className="space-y-4 md:space-y-6">
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-5">
                        <h2 className="text-base font-semibold">Details</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="po-branch">Branch *</Label>
                                <Select
                                    value={branchId}
                                    onValueChange={(value) => { setBranchId(value); setProjectId(''); }}
                                >
                                    <SelectTrigger id="po-branch">
                                        <SelectValue placeholder={
                                            branches.length ? 'Select a branch' : 'No active branches'
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {branches.map((b) => (
                                            <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    The branch is what the approval threshold and the PO number are
                                    measured against.
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="po-required-by">Required by</Label>
                                <Input
                                    id="po-required-by"
                                    type="date"
                                    value={requiredByDate}
                                    onChange={(e) => setRequiredByDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Project</Label>
                            <Combobox
                                options={projects.map((p) => ({
                                    label: `${p.projectName} (${p.projectNumber})`,
                                    value: String(p.id),
                                }))}
                                value={projectId || undefined}
                                onChange={(value) => setProjectId(value || '')}
                                placeholder={branchId ? 'No project (yard / overhead)' : 'Pick a branch first'}
                                searchPlaceholder="Search projects..."
                                emptyMessage="No projects in this branch."
                            />
                        </div>

                        <div>
                            <Label>Vendor *</Label>
                            <Combobox
                                options={vendors.map((v) => ({
                                    label: v.tradingName || v.legalName,
                                    value: String(v.id),
                                    subtitle: v.legalName === v.tradingName ? undefined : v.legalName,
                                }))}
                                value={vendorId || undefined}
                                onChange={(value) => setVendorId(value || '')}
                                placeholder={vendors.length ? 'Select a vendor' : 'No active vendors'}
                                searchPlaceholder="Search vendors..."
                                emptyMessage="No vendors found."
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Vendor is required to submit — a draft can be saved without one.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="po-notes">Notes</Label>
                            <Textarea
                                id="po-notes"
                                rows={3}
                                value={notes}
                                placeholder="Anything AP or the vendor should know — delivery instructions, a quote reference."
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <div>
                            <h2 className="text-base font-semibold">
                                Quotation / attachments *
                            </h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Attach the vendor's quotation (PDF or image) so the price AP is
                                approving is on the record. At least one file is required.
                            </p>
                        </div>
                        <input
                            ref={attachmentInput}
                            type="file"
                            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                addAttachmentFiles(e.target.files);
                                // Reset so picking the same file again still fires onChange.
                                e.target.value = '';
                            }}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={saving !== null}
                            onClick={() => attachmentInput.current?.click()}
                        >
                            <Paperclip size={14} className="mr-1.5" />Attach quotation
                        </Button>
                        {attachments.length > 0 && (
                            <div className="divide-y divide-border border border-border rounded-lg">
                                {attachments.map((file, index) => (
                                    <div key={`${file.name}-${index}`} className="flex items-center gap-3 p-2">
                                        <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                            <FileText size={16} className="text-muted-foreground" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm truncate">{file.name}</span>
                                            <span className="block text-xs text-muted-foreground">
                                                {formatBytes(file.size)}
                                            </span>
                                        </span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Remove ${file.name}`}
                                            disabled={saving !== null}
                                            onClick={() => setAttachments((prev) =>
                                                prev.filter((_, i) => i !== index))}
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <div>
                            <h2 className="text-base font-semibold">Lines *</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Prices are ex GST, AUD. Every receipt is balanced against the
                                quantities you set here.
                            </p>
                        </div>
                        <PurchaseOrderLineEditor
                            lines={lines}
                            onChange={setLines}
                            products={products}
                            disabled={saving !== null}
                        />
                    </CardContent>
                </Card>

                <div className={`rounded-lg border p-3 text-sm ${notice.tone === 'amber'
                    ? 'border-amber-200 bg-amber-50/60 text-amber-900'
                    : 'border-blue-200 bg-blue-50/40 text-blue-900'}`}>
                    <p className="font-medium flex items-center gap-1.5">
                        {notice.tone === 'amber'
                            ? <TriangleAlert size={15} />
                            : <Info size={15} />}
                        {notice.title}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed">{notice.body}</p>
                </div>

                <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                        Order total{' '}
                        <span className="font-semibold text-foreground tabular-nums">{money(total)}</span>
                    </p>
                    <div className="flex flex-col-reverse md:flex-row gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full md:w-auto"
                            disabled={!draftReady}
                            onClick={() => save(false)}
                        >
                            {saving === 'draft' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Save draft
                        </Button>
                        <Button type="submit" className="w-full md:w-auto" disabled={!submitReady}>
                            {saving === 'submit' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Submit
                        </Button>
                    </div>
                </div>
                {blockReason && (
                    <p className="text-xs text-muted-foreground md:text-right">{blockReason}</p>
                )}
            </form>
        </div>
    );
}
